import React, { useState, forwardRef } from 'react';

// ── PasswordInput ─────────────────────────────────────────────
export const PasswordInput = forwardRef(function PasswordInput(
  { className, style, wrapperStyle, ...props },
  ref
) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', ...wrapperStyle }}>
      <input
        ref={ref}
        type={show ? 'text' : 'password'}
        className={className}
        style={{ paddingRight: '40px', width: '100%', ...style }}
        {...props}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        tabIndex={-1}
        style={{
          position: 'absolute', right: '10px',
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '4px', color: '#9ca3af', fontSize: '18px',
          display: 'flex', alignItems: 'center',
          userSelect: 'none',
        }}
        aria-label={show ? 'Ascunde parola' : 'Arată parola'}
      >
        {show ? '🙈' : '👁'}
      </button>
    </div>
  );
});

// ── Card ─────────────────────────────────────────────────────
export function Card({ children, style = {}, title, action }) {
  return (
    <div style={{ ...cs.card, ...style }}>
      {(title || action) && (
        <div style={cs.cardHeader}>
          {title && <h3 style={cs.cardTitle}>{title}</h3>}
          {action && <div>{action}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

// ── StatusBadge ──────────────────────────────────────────────
const STATUS_CONFIG = {
  inregistrata: { label: 'Înregistrată', bg: '#e6f1fb', color: '#0c447c' },
  in_analiza:   { label: 'În analiză',   bg: '#faeeda', color: '#633806' },
  in_lucru:     { label: 'În lucru',     bg: '#eeedfe', color: '#3c3489' },
  rezolvata:    { label: 'Rezolvată',    bg: '#eaf3de', color: '#27500a' },
  respinsa:     { label: 'Respinsă',     bg: '#fcebeb', color: '#791f1f' },
  arhivata:     { label: 'Arhivată',     bg: '#f1efe8', color: '#444441' },
};

export function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: '#eee', color: '#333' };
  return (
    <span style={{ ...cs.badge, backgroundColor: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

// ── PrioritateBadge ──────────────────────────────────────────
const PRIORITATE_CONFIG = {
  scazuta:  { label: 'Scăzută',  color: '#5f5e5a' },
  normala:  { label: 'Normală',  color: '#185fa5' },
  ridicata: { label: 'Ridicată', color: '#854f0b' },
  urgenta:  { label: 'Urgentă',  color: '#a32d2d' },
};

export function PrioritateBadge({ prioritate }) {
  const cfg = PRIORITATE_CONFIG[prioritate] || { label: prioritate, color: '#333' };
  return <span style={{ ...cs.badge, color: cfg.color, backgroundColor: cfg.color + '18' }}>{cfg.label}</span>;
}

// ── Spinner ──────────────────────────────────────────────────
export function Spinner({ size = 32, message = '' }) {
  return (
    <div style={cs.spinnerWrap}>
      <div style={{ ...cs.spinner, width: size, height: size }} />
      {message && <p style={cs.spinnerMsg}>{message}</p>}
    </div>
  );
}

// ── EmptyState ───────────────────────────────────────────────
export function EmptyState({ icon = '📭', title = 'Nimic aici', text = '', action }) {
  return (
    <div style={cs.empty}>
      <div style={cs.emptyIcon}>{icon}</div>
      <h3 style={cs.emptyTitle}>{title}</h3>
      {text && <p style={cs.emptyText}>{text}</p>}
      {action}
    </div>
  );
}

// ── PageHeader ────────────────────────────────────────────────
export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={cs.pageHeader}>
      <div>
        <h1 style={cs.pageTitle}>{title}</h1>
        {subtitle && <p style={cs.pageSubtitle}>{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

// ── StatCard ─────────────────────────────────────────────────
export function StatCard({ label, value, sub, color = '#2d2d7e' }) {
  return (
    <div style={cs.statCard}>
      <div style={{ ...cs.statValue, color }}>{value}</div>
      <div style={cs.statLabel}>{label}</div>
      {sub && <div style={cs.statSub}>{sub}</div>}
    </div>
  );
}

// ── Button ────────────────────────────────────────────────────
export function Button({ children, onClick, variant = 'primary', disabled, type = 'button', style = {} }) {
  const variants = {
    primary:   { backgroundColor: '#2d2d7e', color: '#fff', border: 'none' },
    secondary: { backgroundColor: 'transparent', color: '#2d2d7e', border: '1.5px solid #2d2d7e' },
    danger:    { backgroundColor: '#e24b4a', color: '#fff', border: 'none' },
    ghost:     { backgroundColor: 'transparent', color: '#555', border: '1.5px solid #ddd' },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{ ...cs.btn, ...variants[variant], ...(disabled ? cs.btnDisabled : {}), ...style }}
    >
      {children}
    </button>
  );
}

// ── Input ─────────────────────────────────────────────────────
export function Input({ label, error, ...props }) {
  return (
    <div style={cs.inputWrap}>
      {label && <label style={cs.inputLabel}>{label}</label>}
      <input style={{ ...cs.input, ...(error ? cs.inputErr : {}) }} {...props} />
      {error && <span style={cs.inputErrMsg}>{error}</span>}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────
const cs = {
  card:        { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '16px' },
  cardHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  cardTitle:   { margin: 0, fontSize: '16px', fontWeight: '600', color: '#1a1a2e' },
  badge:       { display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  spinnerWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px', gap: '12px' },
  spinner:     { border: '3px solid #eee', borderTop: '3px solid #2d2d7e', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  spinnerMsg:  { color: '#888', fontSize: '14px', margin: 0 },
  empty:       { textAlign: 'center', padding: '64px 24px' },
  emptyIcon:   { fontSize: '48px', marginBottom: '16px' },
  emptyTitle:  { margin: '0 0 8px', fontSize: '18px', fontWeight: '600', color: '#1a1a2e' },
  emptyText:   { margin: '0 0 24px', color: '#888', fontSize: '15px' },
  pageHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  pageTitle:   { margin: '0 0 4px', fontSize: '24px', fontWeight: '700', color: '#1a1a2e' },
  pageSubtitle:{ margin: 0, color: '#666', fontSize: '14px' },
  statCard:    { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textAlign: 'center' },
  statValue:   { fontSize: '36px', fontWeight: '700', marginBottom: '4px' },
  statLabel:   { fontSize: '13px', color: '#666', fontWeight: '500' },
  statSub:     { fontSize: '12px', color: '#aaa', marginTop: '4px' },
  btn:         { padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'opacity .15s' },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  inputWrap:   { display: 'flex', flexDirection: 'column', gap: '6px' },
  inputLabel:  { fontSize: '14px', fontWeight: '500', color: '#333' },
  input:       { padding: '10px 14px', fontSize: '15px', border: '1.5px solid #ddd', borderRadius: '8px', outline: 'none', fontFamily: 'inherit' },
  inputErr:    { borderColor: '#e24b4a' },
  inputErrMsg: { fontSize: '12px', color: '#e24b4a' },
};
